class VideoCardShowcase extends HTMLElement {
	constructor() {
		super();
		this.currentIndex = 0;
		this.isDesktop = false;
	}

	connectedCallback() {
		this.render();
		this.setupEventListeners();
		this.updateResponsiveLayout();

		// 确保分页点的初始状态正确
		setTimeout(() => {
			this.updateMobilePaginationDots();
		}, 100);

		// 监听窗口大小变化
		window.addEventListener('resize', () => {
			this.updateResponsiveLayout();
		});
	}

	get data() {
		try {
			const dataAttr = this.getAttribute('data') || '{}';
			// 处理Liquid模板的escape，使用decodeURIComponent
			const decodedData = decodeURIComponent(
				dataAttr.replace(/&quot;/g, '"').replace(/&amp;/g, '&')
			);
			return JSON.parse(decodedData);
		} catch (e) {
			console.error('解析媒体数据失败:', e);
			return {};
		}
	}

	render() {
		const {title = '媒体展示', media = []} = this.data;

		// 如果没有媒体数据，显示提示信息
		if (media.length === 0) {
			this.innerHTML = `
        <div class="bg-black w-full py-12 px-4">
          <h2 class="text-white text-4xl font-bold text-center mb-12 font-sans">
            ${title}
          </h2>
          <div class="text-center text-gray-400">
            <p>请在主题设置中添加媒体内容</p>
          </div>
        </div>
      `;
			return;
		}

		this.innerHTML = `
      <div class="bg-black w-full py-12 px-4">
        <!-- 标题 -->
        <h2 class="text-white text-4xl lg:text-6xl font-bold text-center mb-12 font-sans">
          ${this.escapeHtml(title)}
        </h2>

        <!-- 媒体卡片容器 -->
        <div class="relative mx-auto max-w-[1200px]">
          <!-- 桌面端轮播布局 -->
          <div class="desktop-carousel hidden lg:block">
            <div class="carousel-container flex justify-center overflow-x-auto hide-scrollbar scroll-smooth gap-4" id="desktop-carousel">
              ${media
								.map((mediaItem, index) =>
									this.createMediaCard(mediaItem, index, false)
								)
								.join('')}
            </div>

            <!-- 桌面端箭头导航 -->
            <div class="flex justify-center mt-8 space-x-4" id="desktop-navigation">
              <button
                class="arrow-btn prev-btn bg-gray-700 hover:bg-gray-600 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-300 disabled:opacity-50"
                id="prev-btn"
                aria-label="上一页"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>
              <button
                class="arrow-btn next-btn bg-gray-700 hover:bg-gray-600 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors duration-300 disabled:opacity-50"
                id="next-btn"
                aria-label="下一页"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- 移动端轮播布局 -->
          <div class="mobile-carousel lg:hidden">
            <div class="carousel-container flex overflow-x-auto hide-scrollbar scroll-smooth snap-x snap-mandatory" id="mobile-carousel">
              ${media
								.map((mediaItem, index) =>
									this.createMediaCard(mediaItem, index, true)
								)
								.join('')}
            </div>

            <!-- 移动端分页导航点 -->
            <div class="flex justify-center mt-6 space-x-3" id="mobile-pagination">
              ${media
								.map(
									(_, index) => `
                <button
                  class="pagination-dot w-3 h-3 rounded-full transition-all duration-300 bg-gray-600"
                  data-index="${index}"
                  aria-label="跳转到第${index + 1}个媒体"
                ></button>
              `
								)
								.join('')}
            </div>
          </div>
        </div>
      </div>

      <style>
        .pagination-dot {
          cursor: pointer;
          transition: all 0.3s ease;
          transform-origin: center;
        }

        .pagination-dot:hover {
          transform: scale(1.2);
        }

        .pagination-dot.active {
          background: linear-gradient(45deg, #6366f1, #8b5cf6);
          transform: scale(1.3);
          box-shadow: 0 0 12px rgba(139, 92, 246, 0.7);
        }

        /* 移动端轮播样式优化 */
        @media (max-width: 1023px) {
          .carousel-container {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
        }
      </style>
    `;
	}

	createMediaCard(mediaItem, index, isMobile = false) {
		const cardClasses = isMobile
			? 'flex-none w-[96%] px-2 snap-center'
			: 'flex-none flex-shrink-0 w-[36rem]';

		// 确保标题被正确转义
		const mediaTitle = this.escapeHtml(mediaItem.title || '');

		// 根据媒体类型创建不同的内容
		let mediaContent = '';

		if (mediaItem.type === 'video') {
			const videoId = this.escapeHtml(mediaItem.id || '');
			mediaContent = `
        <div class="relative aspect-video">
          <iframe
            class="w-full h-full rounded-t-lg"
            src="https://www.youtube.com/embed/${videoId}"
            title="YouTube video player - ${mediaTitle}"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
            loading="lazy">
          </iframe>
        </div>
      `;
		} else if (mediaItem.type === 'image') {
			const imageUrl = this.escapeHtml(mediaItem.url || '');
			const imageAlt = this.escapeHtml(mediaItem.alt || mediaTitle);
			const hasLink = mediaItem.link && mediaItem.link.trim() !== '';
			const imageLink = hasLink ? this.escapeHtml(mediaItem.link) : '';

			mediaContent = `
        <div class="relative aspect-video">
          ${
						hasLink ? `<a href="${imageLink}" class="block w-full h-full">` : ''
					}
            <img
              class="w-full h-full object-cover rounded-t-lg ${
								hasLink
									? 'hover:opacity-90 transition-opacity duration-300'
									: ''
							}"
              src="${imageUrl}"
              alt="${imageAlt}"
              loading="lazy"
            />
            ${
							hasLink
								? `
              <div class="absolute top-2 right-2 bg-black bg-opacity-50 rounded-full p-2">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </div>
            `
								: ''
						}
          ${hasLink ? '</a>' : ''}
        </div>
      `;
		}

		return `
      <div class="${cardClasses}">
        <div class=" rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
          <!-- 媒体区域 -->
          ${mediaContent}

          <!-- 文本区域 -->
          <div class="p-4">
            <h3 class="text-white text-2xl font-medium text-center leading-tight">
              ${mediaTitle}
            </h3>
          </div>
        </div>
      </div>
    `;
	}

	setupEventListeners() {
		this.setupMobileCarousel();
		this.setupDesktopCarousel();
		this.setupKeyboardNavigation();
	}

	setupMobileCarousel() {
		const carousel = this.querySelector('#mobile-carousel');
		const paginationDots = this.querySelectorAll('.pagination-dot');

		if (!carousel || !paginationDots.length) return;

		// 轮播滚动监听
		let scrollTimeout;
		carousel.addEventListener('scroll', () => {
			clearTimeout(scrollTimeout);
			scrollTimeout = setTimeout(() => {
				this.updateMobileCurrentIndex();
			}, 100);
		});

		// 分页点击事件
		paginationDots.forEach((dot, index) => {
			dot.addEventListener('click', (e) => {
				e.preventDefault();
				this.scrollMobileToIndex(index);
			});
		});

		// 触摸滑动支持
		let startX = 0;
		let scrollLeft = 0;
		let isScrolling = false;

		carousel.addEventListener('touchstart', (e) => {
			startX = e.touches[0].pageX - carousel.offsetLeft;
			scrollLeft = carousel.scrollLeft;
			isScrolling = true;
		});

		carousel.addEventListener('touchmove', (e) => {
			if (!startX || !isScrolling) return;
			e.preventDefault();
			const x = e.touches[0].pageX - carousel.offsetLeft;
			const walk = (x - startX) * 2;
			carousel.scrollLeft = scrollLeft - walk;
		});

		carousel.addEventListener('touchend', () => {
			startX = 0;
			isScrolling = false;
		});
	}

	setupDesktopCarousel() {
		const carousel = this.querySelector('#desktop-carousel');
		const prevBtn = this.querySelector('#prev-btn');
		const nextBtn = this.querySelector('#next-btn');

		if (!carousel || !prevBtn || !nextBtn) return;

		// 箭头按钮点击事件
		prevBtn.addEventListener('click', () => {
			this.scrollDesktopPrev();
		});

		nextBtn.addEventListener('click', () => {
			this.scrollDesktopNext();
		});

		// 滚动监听，更新按钮状态
		carousel.addEventListener('scroll', () => {
			this.updateDesktopNavigationButtons();
		});

		// 初始化按钮状态
		setTimeout(() => {
			this.updateDesktopNavigationButtons();
		}, 100);
	}

	setupKeyboardNavigation() {
		// 键盘导航支持
		this.addEventListener('keydown', (e) => {
			if (e.key === 'ArrowLeft') {
				e.preventDefault();
				if (this.isDesktop) {
					this.scrollDesktopPrev();
				} else {
					this.scrollMobileToIndex(Math.max(0, this.currentIndex - 1));
				}
			} else if (e.key === 'ArrowRight') {
				e.preventDefault();
				if (this.isDesktop) {
					this.scrollDesktopNext();
				} else {
					const maxIndex = this.data.media ? this.data.media.length - 1 : 0;
					this.scrollMobileToIndex(Math.min(maxIndex, this.currentIndex + 1));
				}
			}
		});
	}

	updateMobileCurrentIndex() {
		const carousel = this.querySelector('#mobile-carousel');
		if (!carousel) return;

		const containerWidth = carousel.offsetWidth;
		const cardWidth = containerWidth * 0.96; // 96% 宽度
		const scrollLeft = carousel.scrollLeft;
		const newIndex = Math.round(scrollLeft / cardWidth);

		if (newIndex !== this.currentIndex) {
			this.currentIndex = newIndex;
			this.updateMobilePaginationDots();
		}
	}

	updateMobilePaginationDots() {
		const dots = this.querySelectorAll('.pagination-dot');
		dots.forEach((dot, index) => {
			if (index === this.currentIndex) {
				dot.classList.remove('bg-gray-600');
				dot.classList.add('active');
				dot.style.background = 'linear-gradient(45deg, #6366f1, #8b5cf6)';
				dot.setAttribute('aria-pressed', 'true');
			} else {
				dot.classList.remove('active');
				dot.classList.add('bg-gray-600');
				dot.style.background = '';
				dot.setAttribute('aria-pressed', 'false');
			}
		});
	}

	scrollMobileToIndex(index) {
		const carousel = this.querySelector('#mobile-carousel');
		if (!carousel) return;

		this.currentIndex = index;
		const containerWidth = carousel.offsetWidth;
		const cardWidth = containerWidth * 0.96; // 96% 宽度
		carousel.scrollTo({
			left: index * cardWidth,
			behavior: 'smooth'
		});

		// 立即更新分页点状态
		this.updateMobilePaginationDots();
	}

	scrollDesktopPrev() {
		const carousel = this.querySelector('#desktop-carousel');
		if (!carousel) return;

		const cardWidth = 320 + 24; // 卡片宽度 + margin
		const scrollAmount = cardWidth * 2; // 每次滚动2个卡片的距离

		carousel.scrollBy({
			left: -scrollAmount,
			behavior: 'smooth'
		});
	}

	scrollDesktopNext() {
		const carousel = this.querySelector('#desktop-carousel');
		if (!carousel) return;

		const cardWidth = 320 + 24; // 卡片宽度 + margin
		const scrollAmount = cardWidth * 2; // 每次滚动2个卡片的距离

		carousel.scrollBy({
			left: scrollAmount,
			behavior: 'smooth'
		});
	}

	updateDesktopNavigationButtons() {
		const carousel = this.querySelector('#desktop-carousel');
		const prevBtn = this.querySelector('#prev-btn');
		const nextBtn = this.querySelector('#next-btn');
		const navigation = this.querySelector('#desktop-navigation');

		if (!carousel || !prevBtn || !nextBtn || !navigation) return;

		const scrollLeft = carousel.scrollLeft;
		const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;

		// 检查是否需要滚动 - 如果内容宽度小于等于容器宽度，则隐藏导航
		if (maxScrollLeft <= 0) {
			navigation.style.display = 'none';
			return;
		} else {
			navigation.style.display = 'flex';
		}

		// 更新上一页按钮状态
		if (scrollLeft <= 0) {
			prevBtn.disabled = true;
			prevBtn.classList.add('opacity-50');
		} else {
			prevBtn.disabled = false;
			prevBtn.classList.remove('opacity-50');
		}

		// 更新下一页按钮状态
		if (scrollLeft >= maxScrollLeft - 1) {
			nextBtn.disabled = true;
			nextBtn.classList.add('opacity-50');
		} else {
			nextBtn.disabled = false;
			nextBtn.classList.remove('opacity-50');
		}
	}

	updateResponsiveLayout() {
		const desktopCarousel = this.querySelector('.desktop-carousel');
		const mobileCarousel = this.querySelector('.mobile-carousel');

		this.isDesktop = window.innerWidth >= 1024;

		if (this.isDesktop) {
			// 桌面端
			if (desktopCarousel) desktopCarousel.classList.remove('hidden');
			if (mobileCarousel) mobileCarousel.classList.add('lg:hidden');

			// 延迟更新按钮状态，确保DOM已渲染
			setTimeout(() => {
				this.updateDesktopNavigationButtons();
			}, 100);
		} else {
			// 移动端
			if (desktopCarousel) desktopCarousel.classList.add('hidden');
			if (mobileCarousel) mobileCarousel.classList.remove('lg:hidden');
		}
	}

	// HTML转义函数，防止XSS攻击
	escapeHtml(text) {
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	}
}

// 注册自定义元素
if (!customElements.get('video-card-showcase')) {
	customElements.define('video-card-showcase', VideoCardShowcase);
}
